import React from "react";
import { Box, Stepper, Step, StepLabel, Typography } from "@mui/material";
import { useLoginState } from "./LoginState";
import { SigninContainer } from "./styles";
import { ProfileInterface } from "ts-interfaces";
import SignupForm from "./SignupForm";
import ProfileForm from "./ProfileForm";
import { useAuthModal } from "./AuthModal";
import { PrimaryButton } from "../Button";
import {
  DEFAULT_SIGNUP_LANDING,
  beginStartOptionsLanding,
  startOptionsLandingFor,
} from "../../Home/onboarding/startOptionsExperiment";

const steps = ["First step", "Second step", "Welcome!"];

// Signups on these pages stay where they are after the success step.
const skipRedirectPaths = ['/design-stream*', '/project*', '/board/*', '/materials/*', '/mcp-connect'];
const isSkipRedirectPath = (currentPath: string) =>
  skipRedirectPaths.some(path => {
    if (path.endsWith('*')) {
      return currentPath.startsWith(path.slice(0, -1));
    }
    return currentPath === path;
  });

const SuccessView = ({ onComplete }) => {
  React.useEffect(() => {
    const timer = setTimeout(onComplete, 4000);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <>
      <Typography component="h1" variant="h5" className="login-title" sx={{ paddingBottom: "10px" }}>
        Welcome to Mattoboard!
      </Typography>
      <div className="login-form">
        <div className="success-second-text">
          Your account is ready. Let's start creating.
        </div>
        <PrimaryButton fullWidth variant="contained" onClick={onComplete}>
          Get Started
        </PrimaryButton>
      </div>
    </>
  );
};

export const SignUp = (props) => {
  const setShowLogin = useLoginState((state) => state.setShowLogin);
  const [activeStep, setActiveStep] = React.useState(props.activeStep || 0);
  const profile:ProfileInterface | any = useLoginState((state) => state.profile);
  const { setCloseHidden } = useAuthModal();

  React.useEffect(() => {
    setCloseHidden(activeStep === 1);
    return () => setCloseHidden(false);
  }, [activeStep, setCloseHidden]);

  // The SuccessView timer and the button can both call completeRedirect —
  // the /starting-options hand-off navigates once.
  const redirectingRef = React.useRef(false);

  // Stable reference so SuccessView's 4s timer effect doesn't reset on
  // every SignUp re-render. setShowLogin from zustand is itself stable.
  const completeRedirect = React.useCallback(() => {
    const redirectToFromStorage = sessionStorage.getItem("redirectTo");
    const fallbackRedirect = DEFAULT_SIGNUP_LANDING;
    const targetRedirect = redirectToFromStorage || fallbackRedirect;
    const shouldSkipRedirect = isSkipRedirectPath(window.location.pathname);

    const clearModal = () => {
      try {
        setShowLogin(null);
      } catch (error) {
        console.warn("[SignUp] Unable to close modal after signup:", error);
      }
    };

    // New desktop signups with no real redirectTo land on /starting-options,
    // which stamps the arm once it loads. The modal stays up while the page
    // unloads: clearing it first would let StartLoginDs SPA-hop to the library.
    const startOptionsLanding =
      !shouldSkipRedirect && targetRedirect === fallbackRedirect
        ? startOptionsLandingFor(useLoginState.getState?.()?.profile?.uid)
        : null;
    if (startOptionsLanding) {
      if (redirectingRef.current) return;
      redirectingRef.current = true;
      beginStartOptionsLanding();
      sessionStorage?.removeItem("redirectTo");
      window.location.href = startOptionsLanding;
      return;
    }

    if (!shouldSkipRedirect) {
      if (!redirectToFromStorage) {
        sessionStorage?.setItem("redirectTo", fallbackRedirect);
      }
      clearModal();
      sessionStorage?.removeItem("redirectTo");
      window.location.href = targetRedirect;
      return;
    }
    clearModal();
  }, [setShowLogin]);

  return (
    <SigninContainer>
      <Box className="form-root signup">
        <Box className="stepper-root">
          <Stepper activeStep={activeStep}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
        <Box className="signup-content">
          {activeStep === 0 && (
            <SignupForm onSuccess={() => {
              setActiveStep(1);
              if (!sessionStorage?.getItem("redirectTo")) {
                sessionStorage?.setItem("redirectTo", DEFAULT_SIGNUP_LANDING);
              }
            }} />
          )}
          {activeStep === 1 && profile?.uid && (
            <ProfileForm createProfile={true} initialProfile={profile} onSuccess={() => setActiveStep(2)} />
          )}
          {activeStep === 2 && (
            <SuccessView onComplete={completeRedirect} />
          )}
        </Box>
      </Box>

    </SigninContainer>
  );
};

//q46misn7rv
